import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutGrid,
  Search,
  Star,
  X,
  ChevronRight,
  ChevronDown,
  Home,
  ArrowUpCircle,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Sheet, SheetContent } from '@/shared/components/ui/sheet'
import { usePermissions } from '@/shared/lib/permissions/use-permissions'
import { useNavFavoritesStore } from '@/shared/stores/nav-favorites-store'
import { NAV_SECTIONS, type NavItem, type NavSection } from './nav-config'
import {
  isNavItemVisible,
  isNavSectionVisible,
  visibleItemsWithSubheaders,
  visibleNavGroups,
} from './nav-visibility'
import { NavItemCountPill, NavMenuCountDot, NavSectionCountDot } from './nav-count'

// ─── Helpers ────────────────────────────────────────────

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1160 : true,
  )
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1160px)')
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktop
}

/** Flatten all items from sections that have `items` or `groups`, paired with their section */
function getAllItems(sections: NavSection[]): Array<{ section: NavSection; item: NavItem }> {
  return sections.flatMap((section) => {
    if (section.items) {
      return section.items.map((item) => ({ section, item }))
    }
    if (section.groups) {
      return section.groups.flatMap((group) =>
        group.items.map((item) => ({ section, item })),
      )
    }
    return []
  })
}

/** Find the section key whose items contain a path matching the current location */
function findSectionForPath(pathname: string, sections: NavSection[]): string | null {
  for (const section of sections) {
    // Check direct path
    if (section.path && (pathname === section.path || pathname.startsWith(section.path + '/'))) {
      return section.key
    }
    // Check flat items
    if (section.items?.some((item) => pathname === item.path || pathname.startsWith(item.path + '/'))) {
      return section.key
    }
    // Check groups
    if (section.groups?.some((group) =>
      group.items.some((item) => pathname === item.path || pathname.startsWith(item.path + '/')),
    )) {
      return section.key
    }
  }
  return null
}

// ─── Main export ────────────────────────────────────────

export function ServicesPanel() {
  const [open, setOpen] = useState(false)
  const isDesktop = useIsDesktop()

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 gap-1.5 px-3 text-xs font-normal text-topbar-foreground/90 hover:bg-white/10 hover:text-white focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-0"
        onClick={() => setOpen((v) => !v)}
      >
        <LayoutGrid className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Services</span>
        <NavMenuCountDot />
      </Button>

      {isDesktop ? (
        <DesktopPanel open={open} onClose={() => setOpen(false)} />
      ) : (
        <MobilePanel open={open} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

// ─── Desktop: floating popover ──────────────────────────

interface PanelProps {
  open: boolean
  onClose: () => void
}

function DesktopPanel({ open, onClose }: PanelProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const panelRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [selectedSection, setSelectedSection] = useState<string | null>(null)
  const [activeOnly, setActiveOnly] = useState(
    () => localStorage.getItem('th-active-only') === 'true',
  )

  const { user } = usePermissions()
  const hasAnyPermissions =
    user !== null && (user.isSuperAdmin || Object.keys(user.permissions).length > 0)

  const { favorites, toggleFavorite, isFavorite } = useNavFavoritesStore()

  function handleToggleActiveOnly() {
    setActiveOnly((prev) => {
      const next = !prev
      localStorage.setItem('th-active-only', String(next))
      return next
    })
  }

  const isItemVisible = useCallback(
    (item: NavItem): boolean =>
      isNavItemVisible(item, { user, activeOnly, hasAnyPermissions }),
    [user, hasAnyPermissions, activeOnly],
  )

  // Sections with visible items or groups (excludes direct-link only sections from sidebar)
  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.filter((section) => {
        if (activeOnly && section.path && section.status === 'inactive') return false
        if (section.items) return section.items.some(isItemVisible)
        if (section.groups) return visibleNavGroups(section.groups, isItemVisible).length > 0
        return false // direct-link sections (Home, Roadmap) are shown as special buttons
      }),
    [isItemVisible, activeOnly],
  )

  // On open: select the section matching the current page, or fall back to first
  useEffect(() => {
    if (open) {
      const match = findSectionForPath(pathname, visibleSections)
      setSelectedSection(match ?? visibleSections[0]?.key ?? null)
      setQuery('')
      setTimeout(() => inputRef.current?.focus(), 50)
    } else {
      setSelectedSection(null)
    }
  }, [open, pathname, visibleSections])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Close on click outside
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    // Delay to avoid closing on the trigger button click
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handler)
    }
  }, [open, onClose])

  // Search across all items
  const allItems = useMemo(() => getAllItems(visibleSections), [visibleSections])

  const searchResults = useMemo(() => {
    if (!query.trim()) return null
    const q = query.toLowerCase()
    return allItems.filter(({ item }) => {
      const label = t(item.labelKey).toLowerCase()
      const tags = item.searchTagsKey ? t(item.searchTagsKey).toLowerCase() : ''
      return label.includes(q) || tags.includes(q)
    })
  }, [query, allItems, t])

  // Favorite items resolved from config
  const favoriteItems = useMemo(
    () => allItems.filter(({ item }) => favorites.includes(item.key)),
    [allItems, favorites],
  )

  const currentSection = visibleSections.find((s) => s.key === selectedSection)
  // For flat-items sections: render as list. For groups sections: render as columns.
  const isGroupSection = !!currentSection?.groups
  const sectionItems = currentSection?.items?.filter(isItemVisible) ?? []

  function handleNavigate(path: string) {
    void navigate(path)
    onClose()
  }

  if (!open) return null

  // Wider panel when showing mega-menu groups
  const panelWidth = isGroupSection ? 'w-[960px]' : 'w-[780px]'

  return (
    <div
      ref={panelRef}
      className={`fixed left-4 top-14 z-50 ${panelWidth} max-h-[80vh] flex flex-col rounded-lg border bg-popover text-popover-foreground shadow-xl animate-in fade-in-0 zoom-in-95 slide-in-from-top-2`}
    >
      {/* Search + Active Only toggle */}
      <div className="flex items-center border-b px-3">
        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('servicesPanel.searchPlaceholder')}
          className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
        <button
          type="button"
          onClick={handleToggleActiveOnly}
          className={`mr-2 shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
            activeOnly
              ? 'border-emerald-500/50 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-500/30'
              : 'border-border text-muted-foreground hover:bg-accent'
          }`}
        >
          <span className={`inline-block h-2 w-2 rounded-full ${activeOnly ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
          {t('servicesPanel.activeOnly')}
        </button>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Search results mode */}
      {searchResults ? (
        <div className="flex-1 overflow-y-auto p-2">
          {searchResults.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('common.noResults')}
            </p>
          ) : (
            (() => {
              // Group by section
              const groups = new Map<string, typeof searchResults>()
              for (const r of searchResults) {
                const key = r.section.key
                if (!groups.has(key)) groups.set(key, [])
                groups.get(key)!.push(r)
              }
              return Array.from(groups.entries()).map(([sectionKey, items]) => {
                const section = items[0]!.section
                const SectionIcon = section.icon
                return (
                  <div key={sectionKey} className="mb-2">
                    <div className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                      <SectionIcon className="h-3 w-3" />
                      {t(section.labelKey)}
                    </div>
                    {items.map(({ item }) => (
                      <NavItemRow
                        key={item.key}
                        item={item}
                        isFav={isFavorite(item.key)}
                        onToggleFav={() => toggleFavorite(item.key)}
                        onClick={() => handleNavigate(item.path)}
                        t={t}
                      />
                    ))}
                  </div>
                )
              })
            })()
          )}
        </div>
      ) : (
        <>
          {/* Favorites */}
          {favoriteItems.length > 0 && (
            <div className="border-b px-3 py-2">
              <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                <Star className="h-3 w-3" />
                {t('servicesPanel.favorites')}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {favoriteItems.map(({ section, item }) => {
                  const ItemIcon = item.icon
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
                      onClick={() => handleNavigate(item.path)}
                    >
                      {ItemIcon && <ItemIcon className="h-3 w-3 text-muted-foreground" />}
                      <span className="text-muted-foreground">{t(section.labelKey)}</span>
                      <span className="text-muted-foreground/50">/</span>
                      {t(item.labelKey)}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Two-column: sections | items or mega-menu */}
          <div className="flex flex-1 min-h-0">
            {/* Left: section list */}
            <div className="w-44 shrink-0 border-r overflow-y-auto py-1">
              {/* Home direct link */}
              <button
                type="button"
                className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                onClick={() => handleNavigate('/dashboard')}
              >
                <Home className="h-4 w-4 text-muted-foreground" />
                {t('nav.home')}
              </button>
              {/* Direct-link sections (e.g. Roadmap) */}
              {NAV_SECTIONS.filter(
                (section) =>
                  section.path !== undefined &&
                  section.key !== 'home' &&
                  isNavSectionVisible(section, { user, activeOnly }),
              ).map((section) => {
                const SectionIcon = section.icon
                return (
                  <button
                    key={section.key}
                    type="button"
                    className="flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                    onClick={() => handleNavigate(section.path!)}
                  >
                    <SectionIcon className="h-4 w-4 text-muted-foreground" />
                    {t(section.labelKey)}
                  </button>
                )
              })}
              <div className="mx-2 my-1 h-px bg-border" />
              {visibleSections.map((section) => {
                const SectionIcon = section.icon
                const isActive = section.key === selectedSection
                return (
                  <button
                    key={section.key}
                    type="button"
                    className={`flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? 'bg-accent text-accent-foreground font-medium'
                        : 'hover:bg-accent/50 hover:text-accent-foreground'
                    }`}
                    onClick={() => setSelectedSection(section.key)}
                  >
                    <SectionIcon className="h-4 w-4 text-muted-foreground" />
                    <span className="truncate">{t(section.labelKey)}</span>
                    <NavSectionCountDot section={section} />
                    {isActive && <ChevronRight className="h-3 w-3 ml-auto shrink-0 text-muted-foreground" />}
                  </button>
                )
              })}
            </div>

            {/* Right: items or mega-menu columns */}
            <div className="flex-1 overflow-y-auto py-1">
              {isGroupSection ? (
                // Mega-menu: render groups as columns
                <div className="flex gap-0 h-full">
                  {visibleNavGroups(currentSection!.groups!, isItemVisible).map((group) => {
                    const GroupIcon = group.icon
                    const visibleGroupItems = visibleItemsWithSubheaders(group.items, isItemVisible)
                    return (
                      <div key={group.key} className="flex-1 min-w-0 border-r last:border-r-0 px-1 py-1">
                        <div className="flex items-center gap-1.5 px-2 py-1.5 mb-0.5">
                          <GroupIcon className="h-3 w-3 text-muted-foreground" />
                          <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider truncate">
                            {t(group.labelKey)}
                          </span>
                        </div>
                        {visibleGroupItems.map((item) => (
                          <div key={item.key}>
                            {item.subheaderKey && (
                              <div className="flex items-center gap-1.5 px-2 pt-2 pb-1 mt-1 border-t border-border/50">
                                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider truncate">
                                  {t(item.subheaderKey)}
                                </span>
                              </div>
                            )}
                            <NavItemRow
                              item={item}
                              isFav={isFavorite(item.key)}
                              onToggleFav={() => toggleFavorite(item.key)}
                              onClick={() => handleNavigate(item.path)}
                              t={t}
                            />
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              ) : (
                // Flat list
                sectionItems.map((item) => (
                  <NavItemRow
                    key={item.key}
                    item={item}
                    isFav={isFavorite(item.key)}
                    onToggleFav={() => toggleFavorite(item.key)}
                    onClick={() => handleNavigate(item.path)}
                    t={t}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Mobile: full-screen sheet ──────────────────────────

function MobilePanel({ open, onClose }: PanelProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())
  const [activeOnly, setActiveOnly] = useState(
    () => localStorage.getItem('th-active-only') === 'true',
  )

  const { user } = usePermissions()
  const hasAnyPermissions =
    user !== null && (user.isSuperAdmin || Object.keys(user.permissions).length > 0)

  const { favorites, toggleFavorite, isFavorite } = useNavFavoritesStore()

  function handleToggleActiveOnly() {
    setActiveOnly((prev) => {
      const next = !prev
      localStorage.setItem('th-active-only', String(next))
      return next
    })
  }

  const isItemVisible = useCallback(
    (item: NavItem): boolean =>
      isNavItemVisible(item, { user, activeOnly, hasAnyPermissions }),
    [user, hasAnyPermissions, activeOnly],
  )

  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.filter((section) => {
        if (activeOnly && section.path && section.status === 'inactive') return false
        if (section.items) return section.items.some(isItemVisible)
        if (section.groups) return visibleNavGroups(section.groups, isItemVisible).length > 0
        return false
      }),
    [isItemVisible, activeOnly],
  )

  const allItems = useMemo(() => getAllItems(visibleSections), [visibleSections])

  const searchResults = useMemo(() => {
    if (!query.trim()) return null
    const q = query.toLowerCase()
    return allItems.filter(({ item }) => {
      const label = t(item.labelKey).toLowerCase()
      const tags = item.searchTagsKey ? t(item.searchTagsKey).toLowerCase() : ''
      return label.includes(q) || tags.includes(q)
    })
  }, [query, allItems, t])

  const favoriteItems = useMemo(
    () => allItems.filter(({ item }) => favorites.includes(item.key)),
    [allItems, favorites],
  )

  useEffect(() => {
    if (open) {
      setQuery('')
      const match = findSectionForPath(pathname, visibleSections)
      setExpandedSections(match ? new Set([match]) : new Set())
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open, pathname, visibleSections])

  function toggleSection(key: string) {
    setExpandedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function handleNavigate(path: string) {
    void navigate(path)
    onClose()
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="left" className="w-full sm:w-full sm:max-w-full p-0">
        {/* Search header + Active Only toggle */}
        <div className="flex items-center border-b px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('servicesPanel.searchPlaceholder')}
            className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={handleToggleActiveOnly}
            className={`mr-2 shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
              activeOnly
                ? 'border-emerald-500/50 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-500/30'
                : 'border-border text-muted-foreground hover:bg-accent'
            }`}
          >
            <span className={`inline-block h-2 w-2 rounded-full ${activeOnly ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
            {t('servicesPanel.activeOnly')}
          </button>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto max-h-[calc(100vh-3rem)]">
          {/* Search results */}
          {searchResults ? (
            <div className="p-2">
              {searchResults.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {t('common.noResults')}
                </p>
              ) : (
                searchResults.map(({ section, item }) => {
                  const SectionIcon = section.icon
                  return (
                    <div key={item.key}>
                      <NavItemRow
                        item={item}
                        isFav={isFavorite(item.key)}
                        onToggleFav={() => toggleFavorite(item.key)}
                        onClick={() => handleNavigate(item.path)}
                        t={t}
                        sectionHint={
                          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                            <SectionIcon className="h-2.5 w-2.5" />
                            {t(section.labelKey)}
                          </span>
                        }
                      />
                    </div>
                  )
                })
              )}
            </div>
          ) : (
            <>
              {/* Home link */}
              <button
                type="button"
                className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                onClick={() => handleNavigate('/dashboard')}
              >
                <Home className="h-4 w-4 text-muted-foreground" />
                {t('nav.home')}
              </button>

              {/* Direct-link sections (e.g. Roadmap) */}
              {NAV_SECTIONS.filter(
                (section) =>
                  section.path !== undefined &&
                  section.key !== 'home' &&
                  isNavSectionVisible(section, { user, activeOnly }),
              ).map((section) => {
                const SectionIcon = section.icon
                return (
                  <button
                    key={section.key}
                    type="button"
                    className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                    onClick={() => handleNavigate(section.path!)}
                  >
                    <SectionIcon className="h-4 w-4 text-muted-foreground" />
                    {t(section.labelKey)}
                  </button>
                )
              })}

              {/* Favorites */}
              {favoriteItems.length > 0 && (
                <div className="border-t border-b px-4 py-2">
                  <div className="flex items-center gap-1.5 mb-1.5 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                    <Star className="h-3 w-3" />
                    {t('servicesPanel.favorites')}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {favoriteItems.map(({ item }) => {
                      const ItemIcon = item.icon
                      return (
                        <button
                          key={item.key}
                          type="button"
                          className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
                          onClick={() => handleNavigate(item.path)}
                        >
                          {ItemIcon && <ItemIcon className="h-3 w-3 text-muted-foreground" />}
                          {t(item.labelKey)}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Accordion sections */}
              {visibleSections.map((section) => {
                const SectionIcon = section.icon
                const isExpanded = expandedSections.has(section.key)
                const isGroupSection = !!section.groups

                return (
                  <div key={section.key}>
                    <button
                      type="button"
                      className="flex items-center gap-2 w-full px-4 py-2.5 text-sm font-medium hover:bg-accent/50 transition-colors"
                      onClick={() => toggleSection(section.key)}
                    >
                      <SectionIcon className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 text-left">{t(section.labelKey)}</span>
                      <NavSectionCountDot section={section} />
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </button>
                    {isExpanded && (
                      <div className="pb-1">
                        {isGroupSection ? (
                          // Mobile: render groups with sub-headers
                          visibleNavGroups(section.groups!, isItemVisible).map((group) => {
                            const GroupIcon = group.icon
                            return (
                              <div key={group.key}>
                                <div className="flex items-center gap-1.5 px-6 py-1 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                                  <GroupIcon className="h-3 w-3" />
                                  {t(group.labelKey)}
                                </div>
                                {visibleItemsWithSubheaders(group.items, isItemVisible).map((item) => (
                                  <div key={item.key}>
                                    {item.subheaderKey && (
                                      <div className="px-8 pt-2 pb-0.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-t border-border/50 mt-1">
                                        {t(item.subheaderKey)}
                                      </div>
                                    )}
                                    <NavItemRow
                                      item={item}
                                      isFav={isFavorite(item.key)}
                                      onToggleFav={() => toggleFavorite(item.key)}
                                      onClick={() => handleNavigate(item.path)}
                                      t={t}
                                      indent
                                    />
                                  </div>
                                ))}
                              </div>
                            )
                          })
                        ) : (
                          section.items?.filter(isItemVisible).map((item) => (
                            <NavItemRow
                              key={item.key}
                              item={item}
                              isFav={isFavorite(item.key)}
                              onToggleFav={() => toggleFavorite(item.key)}
                              onClick={() => handleNavigate(item.path)}
                              t={t}
                              indent
                            />
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

// ─── Shared item row ────────────────────────────────────

interface NavItemRowProps {
  item: NavItem
  isFav: boolean
  onToggleFav: () => void
  onClick: () => void
  t: (key: string) => string
  indent?: boolean
  sectionHint?: React.ReactNode
}

function NavItemRow({ item, isFav, onToggleFav, onClick, t, indent, sectionHint }: NavItemRowProps) {
  const ItemIcon = item.icon
  const isUpgrade = item.status === 'inactive'

  return (
    <div
      className={`flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors group ${
        indent ? 'ml-4' : ''
      }`}
    >
      <button
        type="button"
        className="flex items-center gap-2 flex-1 min-w-0 text-left cursor-pointer"
        onClick={onClick}
      >
        {ItemIcon && <ItemIcon className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="break-words">{t(item.labelKey)}</span>
        {item.count && <NavItemCountPill count={item.count} />}
        {item.custom ? (
          <span className="inline-flex items-center gap-0.5 shrink-0 rounded-full border border-violet-500/30 bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-500/20">
            <Sparkles className="h-2.5 w-2.5" />
            {t('servicesPanel.custom')}
          </span>
        ) : isUpgrade ? (
          <span className="inline-flex items-center gap-0.5 shrink-0 rounded-full border border-amber-500/30 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-500/20">
            <ArrowUpCircle className="h-2.5 w-2.5" />
            {t('servicesPanel.upgrade')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-0.5 shrink-0 rounded-full border border-emerald-500/30 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-500/20">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            {t('servicesPanel.active')}
          </span>
        )}
        {sectionHint}
      </button>
      <button
        type="button"
        className={`shrink-0 p-0.5 rounded transition-colors ${
          isFav
            ? 'text-amber-500'
            : 'text-muted-foreground/30 opacity-0 group-hover:opacity-100'
        }`}
        onClick={(e) => {
          e.stopPropagation()
          onToggleFav()
        }}
        aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
      >
        <Star className={`h-3.5 w-3.5 ${isFav ? 'fill-current' : ''}`} />
      </button>
    </div>
  )
}
